import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { useWorkflow } from '../WorkflowContext';
import { useHome, channelLabel } from '../HomeContext';
import { useNotes } from '../NotesContext';
import Tag from '../components/Tag';
import { shortLocation, fullAddress, type Account } from '../lib/accounts';
import { skusFor, oppsFor, reorderSignal, isDemoQuery, usd } from '../lib/clientDeepData';
import { useClientDeals, osEditorUrl, createOSDraft } from '../lib/osData';
import type { Order } from '../lib/clientDeepData';
import { auth } from '../lib/firebase';
import { canonicalClientId } from '../lib/dna';
import ClientPortal, { type PortalKind } from '../components/ClientPortal';
import { useRequests } from '../RequestsContext';
import RequestWorkspace from '../components/RequestWorkspace';
import { usePrepress } from '../PrepressContext';
import PrepressWorkspace from '../components/PrepressWorkspace';
import ClientEditor from '../components/ClientEditor';
import ServerRecordsSection, { ServerTiles } from '../components/ServerRecords';
import { useServerRecords, type Tile } from '../lib/serverRecords';

// Manual client-email endpoint (us-central1 gen2 alias). The ONLY path that emails a client.
const SEND_EMAIL_URL = 'https://us-central1-mfx-2026.cloudfunctions.net/sendClientEmail';
type SendType = 'confirmation' | 'status' | 'signed';
const SEND_LABEL: Record<SendType, string> = { confirmation: 'Order confirmation', status: 'Status update', signed: 'Signed receipt' };

type TabKey = 'summary' | 'server' | 'orders' | 'sales' | 'csr' | 'prepress' | 'comms' | 'tasks' | 'notes';
const TABS: [TabKey, string][] = [
  ['summary', 'Summary'], ['server', 'Records from the server'], ['orders', 'Orders · Quotes · POs'], ['sales', 'Sales'], ['csr', 'CSR / Requests'], ['prepress', 'Prepress'], ['comms', 'Communications'], ['tasks', 'Tasks'], ['notes', 'Notes & Comments'],
];

function HealthRing({ value }: { value: number }) {
  const r = 34, c = 2 * Math.PI * r, off = c * (1 - value / 100);
  const color = value >= 80 ? 'var(--gn)' : value >= 60 ? 'var(--or)' : 'var(--rd)';
  return (
    <svg className="ring" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={r} className="ring-bg" />
      <circle cx="40" cy="40" r={r} className="ring-fg" style={{ stroke: color, strokeDasharray: c, strokeDashoffset: off }} />
      <text x="40" y="38" className="ring-val">{value}</text>
      <text x="40" y="52" className="ring-lbl">HEALTH</text>
    </svg>
  );
}

function Fact({ k, v }: { k: string; v?: string | number }) {
  if (v === undefined || v === '' || v === null) return null;
  return <div className="fact"><span className="fact-k">{k}</span><span className="fact-v">{v}</span></div>;
}

export default function ClientProfile() {
  const { demo, user } = useAuth();
  const { data } = useAccounts(demo);
  const wf = useWorkflow();
  const home = useHome();
  const notesCtx = useNotes();
  const R = useRequests();
  const P = usePrepress();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<TabKey>('summary');
  const [comment, setComment] = useState('');
  const [portal, setPortal] = useState<PortalKind | null>(null);
  const [portalFocus, setPortalFocus] = useState<string | undefined>(undefined);
  const [openReqId, setOpenReqId] = useState<string | null>(null);
  const [openPrepId, setOpenPrepId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [emailBusy, setEmailBusy] = useState<string>('');
  const [emailMsg, setEmailMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Manual, human-triggered client email. `test` routes it to the signed-in user instead of the client.
  const sendClientEmail = async (o: Order, type: SendType, test: boolean) => {
    if (!o.soId) return;
    if (!test) {
      if (!o.email) { setEmailMsg({ ok: false, text: `No client email on ${o.id}. Add one to the Sales Order first.` }); return; }
      if (!window.confirm(`Send the ${SEND_LABEL[type]} email for ${o.id} to the client at ${o.email}?`)) return;
    }
    const key = `${o.soId}:${type}:${test ? 't' : 'c'}`;
    setEmailBusy(key); setEmailMsg(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) { setEmailMsg({ ok: false, text: 'Please sign in again to send.' }); setEmailBusy(''); return; }
      const res = await fetch(SEND_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ soId: o.soId, type, test }),
      });
      const d = await res.json().catch(() => ({} as any));
      if (res.ok && d.ok) setEmailMsg({ ok: true, text: `${test ? 'Test ' : ''}${SEND_LABEL[type]} sent to ${d.to}.` });
      else setEmailMsg({ ok: false, text: d.error || `Send failed (${res.status}).` });
    } catch (e: any) {
      setEmailMsg({ ok: false, text: 'Send error: ' + (e?.message || 'network') });
    } finally {
      setEmailBusy('');
    }
  };

  const clients = (data?.accounts ?? []).filter((a) => a.type !== 'vendor');
  const picked: Account | undefined = params.get('id') ? data?.accounts.find((a) => a.id === params.get('id')) : clients[0];
  const client = picked ?? clients[0];
  const id = client?.id ?? 'cust_unknown';
  const name = client?.name ?? 'Client';
  const loc = client ? shortLocation(client) : '—';
  const health = client?.healthScore ?? 82;

  const demoData = isDemoQuery();
  const deals = useClientDeals(client);
  // Records from the server (b726crm): the copy-only libraries, linked by the client's MFX id; twins = the other
  // `customers` doc that carries the same id (36 companies exist under two docs — they show the same records)
  const srv = useServerRecords(client);
  const [srvOpen, setSrvOpen] = useState<Tile | null>(null);
  const twins = client?.mfxId ? clients.filter((a) => a.id !== client.id && a.mfxId === client.mfxId) : [];
  const openServerList = (t: Tile) => { setSrvOpen(t); setTab('server'); };
  const orders = deals.orders, quotes = deals.quotes, pos = deals.pos;
  const skus = useMemo(() => (demoData ? skusFor(id) : []), [id, demoData]);
  const opps = useMemo(() => (demoData ? oppsFor(id) : []), [id, demoData]);
  const rs = useMemo(() => (demoData ? reorderSignal(id) : { due: false, window: '—', confidence: 0 }), [id, demoData]);
  const clientRequests = R.requests.filter((r) => r.clientName === name);
  const clientPrepress = P.jobs.filter((j) => j.clientName === name);
  const myTasks = wf.tasks.filter((t) => t.client === name);
  const openTasks = myTasks.filter((t) => t.status !== 'completed');
  const myComms = home.comms.filter((c) => c.client === name);
  const ceoNotes = notesCtx.notesFor(id);
  const comments = notesCtx.commentsFor(id);
  const author = user?.displayName || 'You';
  const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  const send = () => { if (comment.trim()) { notesCtx.addComment(id, author, comment.trim()); setComment(''); } };

  // Unified longitudinal record feed (Health Connect-style chart)
  const record = useMemo(() => {
    const r: { kind: string; title: string; sub: string; time: string; refId?: string; osQuoteId?: string }[] = [];
    myComms.forEach((c) => r.push({ kind: 'message', title: c.subject, sub: `${channelLabel[c.channel]} · ${c.preview}`, time: c.time }));
    orders.forEach((o) => r.push({ kind: 'order', title: `Order ${o.id} · ${o.product}`, sub: `${usd(o.value)} · ${o.status.replace('_', ' ')}`, time: `due ${o.due}`, refId: o.id }));
    quotes.forEach((q) => r.push({ kind: 'quote', title: `Quote ${q.id} · ${q.product}`, sub: `${usd(q.value)} · ${q.status}`, time: q.age, refId: q.id, osQuoteId: q.osId }));
    openTasks.forEach((t) => r.push({ kind: 'task', title: t.title, sub: `${t.owner} · due ${t.due}`, time: t.status, refId: t.id }));
    wf.timeline.forEach((t) => r.push({ kind: 'note', title: t.title, sub: t.text, time: t.time }));
    return r.slice(0, 12);
  }, [myComms, orders, quotes, openTasks, wf.timeline]);

  const dotColor: Record<string, string> = { message: 'var(--neon-blue)', order: 'var(--gn)', quote: 'var(--yl)', task: 'var(--neon-purple)', note: 'var(--ac)' };
  const openOSQuote = (osId?: string) => { if (osId) window.open(osEditorUrl(osId), '_blank', 'noreferrer'); };
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteErr, setQuoteErr] = useState<string | null>(null);
  const startNewQuote = () => {
    const w = window.open('', '_blank');
    setQuoteBusy(true); setQuoteErr(null);
    createOSDraft({ customerName: name, customerId: id.replace(/^cust_/, '') })
      .then((r) => {
        setQuoteBusy(false);
        if (r.ok && r.id) { if (w) w.location.href = osEditorUrl(r.id); }
        else { if (w) w.close(); setQuoteErr(`Couldn't create the quote — make sure you're signed in to the OS. ${r.error || ''}`); }
      })
      .catch((e) => { setQuoteBusy(false); if (w) w.close(); setQuoteErr('Quote create failed: ' + (e?.message || '')); });
  };
  const openRecord = (e: { kind: string; refId?: string; osQuoteId?: string }) => {
    // Quotes open the real OS quote engine when we have a live OS id; otherwise the portal preview.
    if (e.kind === 'quote') { if (e.osQuoteId) openOSQuote(e.osQuoteId); else { setPortalFocus(e.refId); setPortal('quotes'); } }
    else if (e.kind === 'order') { setPortalFocus(e.refId); setPortal('orders'); }
    else if (e.kind === 'task') { setPortalFocus(e.refId); setPortal('tasks'); }
    else if (e.kind === 'message') { setTab('comms'); }
    else if (e.kind === 'note') { setTab('notes'); }
  };
  const nextDeadline = openTasks[0]?.due || orders[0]?.due || '—';

  return (
    <>
      <div className="profile-topbar">
        <button className="back-link" onClick={() => nav('/clients')}>← All clients</button>
        <button className="btn primary small" onClick={() => setEditOpen(true)}>✎ Edit profile</button>
      </div>

      <div className="chart">
        {/* ── Left identity rail ── */}
        <aside className="chart-rail">
          <div className="rail-id">
            <div className="rail-avatar">{initials}</div>
            <h3>{name}</h3>
            <div className="mono">{canonicalClientId(client)}</div>
            <div className="rail-tags">
              <span className="tag st-completed">Active</span>
              {client?.industry && <span className="tag pr-standard">{client.industry}</span>}
            </div>
          </div>
          <div className="rail-ring"><HealthRing value={health} /></div>
          <div style={{ textAlign: 'center', marginTop: -6, marginBottom: 4 }}>{deals.live ? <span className="live-flag">Live · OS data</span> : demoData ? <span className="demo-flag">Sample data</span> : <span className="demo-flag">No live deals yet</span>}</div>
          <div className="rail-facts">
            <Fact k="Account owner" v="Fatima · CSR" />
            <Fact k="Location" v={loc} />
            <Fact k="Primary contact" v={client?.contactName} />
            <Fact k="Phone" v={client?.phone} />
            <Fact k="Email" v={client?.email} />
            <Fact k="Website" v={client?.website} />
            <Fact k="Source" v={client?.source} />
            <Fact k="Record" v={client?.recordId} />
            <Fact k="MFX id" v={client?.mfxId} />
          </div>
          {(fullAddress(client?.billing) || fullAddress(client?.shipping)) && (
            <div className="rail-facts">
              {fullAddress(client?.billing) && <><div className="section-h">Billing</div><div className="addr-card">{fullAddress(client?.billing)}</div></>}
              {fullAddress(client?.shipping) && <><div className="section-h">Shipping</div><div className="addr-card">{fullAddress(client?.shipping)}</div></>}
            </div>
          )}
        </aside>

        {/* ── Center record ── */}
        <section className="chart-main">
          <div className="chart-stats">
            <button className="cstat clickable" onClick={() => setPortal('orders')}><div className="cstat-v" style={{ color: 'var(--ac)' }}>{orders.length}</div><div className="cstat-l">Active Orders</div><div className="cstat-s">{usd(orders.reduce((s, o) => s + o.value, 0))} · open portal →</div></button>
            <button className="cstat clickable" onClick={() => setPortal('quotes')}><div className="cstat-v" style={{ color: 'var(--yl)' }}>{quotes.length}</div><div className="cstat-l">Open Quotes</div><div className="cstat-s">{usd(quotes.reduce((s, q) => s + q.value, 0))} · open portal →</div></button>
            <button className="cstat clickable" onClick={() => setPortal('pos')}><div className="cstat-v" style={{ color: 'var(--gn)' }}>{pos.length}</div><div className="cstat-l">Incoming POs</div><div className="cstat-s">open portal →</div></button>
            <button className="cstat clickable" onClick={() => setPortal('tasks')}><div className="cstat-v">{openTasks.length}</div><div className="cstat-l">Open Tasks</div><div className="cstat-s">{skus.length} SKUs · open portal →</div></button>
          </div>

          <div className="tabs">
            {TABS.map(([k, label]) => <button key={k} className={`tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{label}</button>)}
          </div>

          {tab === 'summary' && (
            <div className="srv-summary">
              <div className="srv-summary-head"><span className="section-h" style={{ margin: 0 }}>Records from the server</span><span className="page-sub">{srv.link ? (srv.link.mfxId ? `linked by ${srv.link.mfxId}` : `by customers/${srv.link.docId}`) : 'no server link'} · click a tile for the list</span></div>
              <ServerTiles srv={srv} active={null} onOpen={openServerList} />
            </div>
          )}

          {tab === 'server' && (
            <ServerRecordsSection srv={srv} account={client} twins={twins} open={srvOpen} onOpen={setSrvOpen} />
          )}

          {tab === 'summary' && (
            <div className="card">
              <div className="card-header"><div><h3>Client Record</h3><p>One longitudinal view of every touchpoint — messages, orders, quotes, tasks, and notes.</p></div></div>
              <div className="card-body">
                <div className="record">
                  {record.map((e, i) => (
                    <div className="record-row clickable" key={i} onClick={() => openRecord(e)} title="Open in client portal">
                      <span className="record-dot" style={{ background: dotColor[e.kind] }} />
                      <div className="record-main"><strong>{e.title}</strong><p>{e.sub}</p></div>
                      <span className="record-time">{e.time}</span>
                      <span className="record-go">›</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'orders' && (
            <>
              <div className="card">
                <div className="card-header"><div><h3>Active Orders</h3><p>Confirmed sales orders — send the client their confirmation, status update, or signed receipt. Nothing sends until you click.</p></div></div>
                {emailMsg && <div style={{ margin: '0 14px 8px', padding: '8px 12px', borderRadius: 8, fontSize: 12, background: emailMsg.ok ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)', color: emailMsg.ok ? 'var(--gn)' : 'var(--rd)', border: `1px solid ${emailMsg.ok ? 'var(--gn)' : 'var(--rd)'}` }}>{emailMsg.text}</div>}
                <div className="table-wrap"><table><thead><tr><th>Order</th><th>PO</th><th>Product</th><th>Qty</th><th>Value</th><th>Status</th><th>Due</th><th>Send to client</th></tr></thead>
                  <tbody>{orders.length === 0 ? <tr><td colSpan={8}><div className="empty">No active orders.</div></td></tr> : orders.map((o) => {
                    const busy = !!o.soId && emailBusy.startsWith(`${o.soId}:`);
                    return (
                    <tr key={o.id}><td><strong>{o.id}</strong></td><td>{o.po}</td><td>{o.product}</td><td>{o.qty}</td><td>{usd(o.value)}</td><td><Tag kind="st" value={o.status === 'in_production' ? 'in_progress' : o.status === 'shipped' ? 'completed' : 'acknowledged'} /></td><td>{o.due}</td>
                    <td>{o.soId ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn small" disabled={busy} title={o.email ? `Send to ${o.email}` : 'No client email on file'} onClick={() => sendClientEmail(o, 'confirmation', false)}>Confirmation</button>
                        <button className="btn small" disabled={busy} onClick={() => sendClientEmail(o, 'status', false)}>Status</button>
                        <button className="btn small" disabled={busy} onClick={() => sendClientEmail(o, 'signed', false)}>Signed</button>
                        <button className="btn small ghost" disabled={busy} title="Send a test copy to yourself" onClick={() => sendClientEmail(o, 'confirmation', true)}>Test→me</button>
                      </div>
                    ) : <span className="sub">—</span>}</td></tr>
                    );
                  })}</tbody></table></div>
              </div>
              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-header"><div><h3>Pending Quotes</h3><p>Awaiting client decision.</p></div></div>
                <div className="table-wrap"><table><thead><tr><th>Quote</th><th>Product</th><th>Qty</th><th>Value</th><th>Status</th><th>Age</th></tr></thead>
                  <tbody>{quotes.length === 0 ? <tr><td colSpan={6}><div className="empty">No open quotes.</div></td></tr> : quotes.map((q) => (
                    <tr key={q.id} className="row-link" style={{ cursor: 'pointer' }} title="Open in OS Quote Engine" onClick={() => (q.osId ? openOSQuote(q.osId) : (setPortalFocus(q.id), setPortal('quotes')))}><td><strong>{q.id}</strong></td><td>{q.product}</td><td>{q.qty}</td><td>{usd(q.value)}</td><td><span className={`tag pr-${q.status === 'negotiating' ? 'rush' : 'standard'}`}>{q.status}</span></td><td>{q.age}</td></tr>
                  ))}</tbody></table></div>
              </div>
              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-header"><div><h3>Incoming POs &amp; SKUs</h3><p>New client POs and reorder history.</p></div></div>
                <div className="table-wrap"><table><thead><tr><th>PO / SKU</th><th>Detail</th><th>Value / Reorders</th><th>Status</th></tr></thead>
                  <tbody>
                    {pos.map((p, i) => (<tr key={'p' + i}><td><strong>{p.po}</strong></td><td>{p.received} · {p.ship}</td><td>{usd(p.value)}</td><td><Tag kind="st" value={p.status === 'accepted' ? 'completed' : p.status === 'reviewing' ? 'in_progress' : 'new'} /></td></tr>))}
                    {skus.map((s) => (<tr key={s.sku}><td><strong>{s.sku}</strong></td><td>{s.product} · last {s.lastOrder}</td><td>{s.reorders}× reorders</td><td><span className="tag pr-standard">SKU</span></td></tr>))}
                  </tbody></table></div>
              </div>
            </>
          )}

          {tab === 'comms' && (
            <div className="card">
              <div className="card-header"><div><h3>Communications</h3><p>Email, calls, texts, and portal activity with {name}.</p></div></div>
              <div className="card-body"><div className="mini-list">
                {myComms.length === 0 && <div className="empty">No communications logged yet.</div>}
                {myComms.map((c) => (
                  <div className={`comm-row${c.unread ? ' unread' : ''}`} key={c.id}>
                    <span className={`chan-badge ch-${c.channel}`}>{channelLabel[c.channel]}</span>
                    <div className="mr-main"><strong>{c.subject}</strong><div className="mr-sub">{c.preview}</div></div>
                    <span className="comm-time">{c.time}</span>
                  </div>
                ))}
              </div></div>
            </div>
          )}

          {tab === 'tasks' && (
            <div className="card">
              <div className="card-header"><div><h3>Client Tasks</h3><p>Every open and recent task tied to {name}.</p></div><button className="btn small" onClick={() => nav('/tasks')}>Task board →</button></div>
              <div className="card-body"><div className="mini-list">
                {myTasks.length === 0 && <div className="empty">No tasks on record yet.</div>}
                {myTasks.map((t) => (
                  <div className="mini-row" key={t.id}>
                    <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.id} · {t.owner} · due {t.due}</div></div>
                    <div style={{ display: 'flex', gap: 6 }}><Tag kind="dp" value={t.dept} /><Tag kind="st" value={t.status} /></div>
                  </div>
                ))}
              </div></div>
            </div>
          )}

          {tab === 'notes' && (
            <div className="card">
              <div className="card-header"><div><h3>CEO Notes &amp; Staff Comments</h3><p>Leadership guidance and internal discussion.</p></div></div>
              <div className="card-body">
                {ceoNotes.map((n) => (
                  <div className="ceo-note" key={n.id}><div className="ceo-badge">{n.author}</div><p>{n.text}</p><span className="comm-time">{n.time}</span></div>
                ))}
                <div className="comment-thread" style={{ marginTop: 14 }}>
                  {comments.map((c) => (
                    <div className="comment" key={c.id}><div className="comment-head"><strong>{c.author}</strong><span className="comm-time">{c.time}</span></div><p>{c.text}</p></div>
                  ))}
                </div>
                <div className="comment-box">
                  <input placeholder="Add an internal comment…" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
                  <button className="btn primary small" onClick={send}>Post</button>
                </div>
              </div>
            </div>
          )}
          {tab === 'sales' && (
            <>
              <div className="card">
                <div className="card-header"><div><h3>Opportunities</h3><p>Open sales opportunities for this client.</p></div><button className="btn small" onClick={() => nav('/pipeline')}>Pipeline →</button></div>
                <div className="table-wrap"><table><thead><tr><th>Opportunity</th><th>Stage</th><th>Value</th><th>Owner</th><th>Age</th></tr></thead>
                  <tbody>{opps.length === 0 ? <tr><td colSpan={5}><div className="empty">No open opportunities.</div></td></tr> : opps.map((o) => (
                    <tr key={o.id} style={{ cursor: 'default' }}><td><strong>{o.id}</strong> · {o.title}</td><td><span className="tag dp-sales">{o.stage}</span></td><td>{usd(o.value)}</td><td>{o.owner}</td><td>{o.age}</td></tr>
                  ))}</tbody></table></div>
              </div>
              <div className="grid cols-2" style={{ marginTop: 14 }}>
                <div className="card pad"><div className="section-h">Quote Summary</div><Fact k="Open quotes" v={quotes.length} /><Fact k="Quoted value" v={usd(quotes.reduce((s, q) => s + q.value, 0))} /><button className="btn small" style={{ marginTop: 10 }} onClick={() => nav('/quote-engine')}>New quote →</button></div>
                <div className="card pad"><div className="section-h">Reorder Signal</div><Fact k="Due" v={rs.due ? rs.window : 'No signal'} /><Fact k="Confidence" v={`${rs.confidence}%`} />{rs.sku && <Fact k="Top SKU" v={rs.sku.sku} />}<button className="btn small" style={{ marginTop: 10 }} onClick={() => nav('/reorders')}>Reorders →</button></div>
              </div>
            </>
          )}

          {tab === 'csr' && (
            <div className="card">
              <div className="card-header"><div><h3>CSR Requests</h3><p>Every intake request for this client — open to run the lifecycle.</p></div><button className="btn small" onClick={() => nav('/intake')}>Intake →</button></div>
              <div className="card-body"><div className="mini-list">
                {clientRequests.length === 0 && <div className="empty">No requests on record. Create one in the Intake Center.</div>}
                {clientRequests.map((r) => (
                  <div className="mini-row" key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpenReqId(r.id)}>
                    <div className="mr-main"><strong>{r.id} · {r.type}</strong><div className="mr-sub">{r.summary}</div></div>
                    <div style={{ display: 'flex', gap: 6 }}><span className="tag pr-standard">{r.tasks.length} task(s)</span><span className="tag st-in_progress">{r.status.replace('_', ' ')}</span></div>
                  </div>
                ))}
              </div></div>
            </div>
          )}

          {tab === 'prepress' && (
            <div className="card">
              <div className="card-header"><div><h3>Prepress Jobs</h3><p>Artwork validation, proofs, blueprints, and release status.</p></div><button className="btn small" onClick={() => nav('/prepress')}>Prepress →</button></div>
              <div className="card-body"><div className="mini-list">
                {clientPrepress.length === 0 && <div className="empty">No prepress jobs yet. Create an artwork intake in Prepress.</div>}
                {clientPrepress.map((j) => (
                  <div className="mini-row" key={j.id} style={{ cursor: 'pointer' }} onClick={() => setOpenPrepId(j.id)}>
                    <div className="mr-main"><strong>{j.id} · {j.product}</strong><div className="mr-sub">{j.sku || ''}{j.sku ? ' · ' : ''}{j.proofs.length} proof(s){j.blueprint ? ` · ${j.blueprint.id}` : ''}</div></div>
                    <span className={`tag st-${j.status === 'released' ? 'completed' : j.status === 'on_hold' ? 'blocked' : 'in_progress'}`}>{j.status.replace('_', ' ')}</span>
                  </div>
                ))}
              </div></div>
            </div>
          )}

        </section>

        {/* ── Right rail ── */}
        <aside className="chart-side">
          <div className="card pad">
            <div className="section-h">Quick Actions</div>
            <button className="btn primary" style={{ width: '100%', marginBottom: quoteErr ? 6 : 10, display: 'flex', justifyContent: 'center' }} onClick={startNewQuote} disabled={quoteBusy}>{quoteBusy ? 'Creating quote…' : '＋ New Quote'}</button>
            {quoteErr && <div style={{ fontSize: 11, color: 'var(--rd)', marginBottom: 10, lineHeight: 1.4 }}>{quoteErr}</div>}
            <div className="qa-grid">
              <button className="qa" onClick={() => nav('/intake')}>＋ New intake</button>
              <button className="qa" onClick={() => { setTab('comms'); }}>✉ Log message</button>
              <button className="qa" onClick={() => nav('/tasks')}>▤ New task</button>
              <button className="qa" onClick={() => { setTab('notes'); }}>✎ Add note</button>
            </div>
          </div>
          <div className="card pad">
            <div className="section-h">Account Team</div>
            <div className="team">
              {[['FA', 'Fatima', 'CSR / Owner'], ['DI', 'Diego', 'Prepress'], ['KI', 'Kim', 'Logistics'], ['MA', 'Marisol', 'Quality']].map(([ini, nm, role]) => (
                <div className="team-row" key={nm}><div className="team-av">{ini}</div><div><strong>{nm}</strong><div className="mr-sub">{role}</div></div></div>
              ))}
            </div>
          </div>
          <div className="card pad">
            <div className="section-h">Next Best Actions</div>
            <ul className="nba">
              <li>Confirm SKU numbers before logistics prepares samples.</li>
              <li>Follow up on the open quote awaiting approval.</li>
              <li>Verify material receiving date to protect the due date.</li>
            </ul>
          </div>
          <div className="card pad">
            <div className="section-h">Key Dates</div>
            <Fact k="Next deadline" v={nextDeadline} />
            <Fact k="Open value" v={usd(orders.reduce((s, o) => s + o.value, 0) + quotes.reduce((s, q) => s + q.value, 0))} />
          </div>
        </aside>
      </div>

      {openReqId && (() => { const r = R.requests.find((x) => x.id === openReqId); return r ? <RequestWorkspace request={r} onClose={() => setOpenReqId(null)} /> : null; })()}
      {openPrepId && (() => { const j = P.jobs.find((x) => x.id === openPrepId); return j ? <PrepressWorkspace job={j} onClose={() => setOpenPrepId(null)} /> : null; })()}

      {portal && <ClientPortal kind={portal} client={client} orders={orders} quotes={quotes} pos={pos} tasks={openTasks} focusId={portalFocus} onClose={() => { setPortal(null); setPortalFocus(undefined); }} />}

      {editOpen && client && (
        <ClientEditor mode="edit" initial={client} onClose={() => setEditOpen(false)} onSaved={() => setEditOpen(false)} />
      )}
    </>
  );
}
